import * as React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LanguageIcon from "@mui/icons-material/Language";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import {
  colors,
  typography,
  spacing,
} from "../../../../../../../assets/styles/tokens";
import AddTasks from "./AddTrendTasks";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

export interface SharedTaskProps {
  assignee: string;
  dueDate: Dayjs | null;
  priority: string;
  website: string;
}

interface CreateTasksDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateTasksDrawer({
  open,
  onClose,
}: CreateTasksDrawerProps) {
  const [assignee, setAssignee] = React.useState("");
  const [dueDate, setDueDate] = React.useState<Dayjs | null>(null);
  const [priority, setPriority] = React.useState("");
  const [website, setWebsite] = React.useState("");
  const [step, setStep] = React.useState<"shared" | "addTasks">("shared");
  const [hasVisitedAddTasks, setHasVisitedAddTasks] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setStep("shared");
      setHasVisitedAddTasks(false);
      setAssignee("");
      setDueDate(null);
      setPriority("");
      setWebsite("");
    }
  }, [open]);

  // All four shared fields are filled
  const allSharedFilled =
    assignee !== "" && dueDate !== null && priority !== "" && website !== "";

  const buttonLabel = hasVisitedAddTasks
    ? allSharedFilled
      ? "SAVE"
      : "NEXT"
    : allSharedFilled
      ? "NEXT"
      : "SKIP";

  const sharedProps: SharedTaskProps = { assignee, dueDate, priority, website };

  const handleProceed = () => {
    setHasVisitedAddTasks(true);
    setStep("addTasks");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            pb: spacing.Spacing.xl.$value,
          },
        }}
      >
        {step === "shared" ? (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: spacing.Spacing.xl.$value,
                px: spacing.Spacing.xl.$value,
              }}
            >
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: typography.Font_weight.Medium.$value,
                  fontSize: typography.Size.Text.xl.$value,
                  color: colors.GrayLightMode[900].$value,
                }}
              >
                Create Tasks
              </Typography>
              <Box sx={{ width: spacing.Spacing["4xl"].$value }} />
            </Box>
            <Divider />

            {/* Scrollable body */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: spacing.Spacing.xl.$value,
                pt: spacing.Spacing.xl.$value,
              }}
            >
              <Box
                sx={{
                  pt: spacing.Spacing.md.$value,
                  pb: spacing.Spacing["3xl1"].$value,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: typography.Font_weight.Medium.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    color: colors.GrayLightMode[900].$value,
                  }}
                >
                  Shared Properties (Optional)
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.sm.$value,
                    color: colors.GrayDarkMode[700].$value,
                  }}
                >
                  Set defaults for all tasks, or skip to add tasks individually
                </Typography>
              </Box>
              {/* Assignee */}
              <FormControl fullWidth sx={{ mb: spacing.Spacing.xl.$value }}>
                <InputLabel
                  shrink
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    background: colors.Base.White.$value,
                    paddingRight: spacing.Spacing.xs.$value,
                    "&.Mui-focused": {
                      color: colors.Brand[600].$value,
                    },
                  }}
                >
                  Assignee
                </InputLabel>
                <Select
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  displayEmpty
                  label="Assignee"
                  size="small"
                  startAdornment={
                    <InputAdornment position="start">
                      <AccountCircleIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayDarkMode[700].$value }}
                      />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: spacing.Radius.md.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    "& .MuiSelect-select": {
                      paddingTop: spacing.Spacing.md1.$value,
                      paddingBottom: spacing.Spacing.md1.$value,
                      color: colors.GrayLightMode[900].$value,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  }}
                >
                  <MenuItem value="">Select Assignee</MenuItem>
                  <MenuItem value="Lama Hadid">Lama Hadid</MenuItem>
                  <MenuItem value="Ortal Adiv">Ortal Adiv</MenuItem>
                  <MenuItem value="Sagar Sarkar">Sagar Sarkar</MenuItem>
                </Select>
              </FormControl>

              {/* Due Date */}
              <DatePicker
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                format="DD/MM/YYYY"
                slots={{
                  openPickerIcon: CalendarTodayIcon,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    label: "Due Date",
                    sx: {
                      mb: spacing.Spacing.xl.$value,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: spacing.Radius.md.$value,
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.Brand[600].$value,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.Brand[600].$value,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: colors.Brand[600].$value,
                      },
                      fontSize: typography.Size.Text.sm.$value,
                    },
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon
                            fontSize="small"
                            sx={{
                              color: colors.GrayDarkMode[700].$value,
                              mr: spacing.Spacing.xxs.$value,
                            }}
                          />
                        </InputAdornment>
                      ),
                    },
                  },
                  openPickerButton: {
                    sx: { display: "none" },
                  },
                  actionBar: {
                    sx: {
                      "& .MuiButton-root": {
                        color: colors.Brand[700].$value,
                      },
                      "& .MuiButton-root:last-of-type": {
                        color: colors.Brand[600].$value,
                      },
                    },
                  },
                }}
              />
              {/* Priority */}
              <FormControl fullWidth sx={{ mb: spacing.Spacing.xl.$value }}>
                <InputLabel
                  shrink
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    background: colors.Base.White.$value,
                    paddingRight: spacing.Spacing.xs.$value,
                    "&.Mui-focused": {
                      color: colors.Brand[600].$value,
                    },
                  }}
                >
                  Priority
                </InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  displayEmpty
                  notched
                  label="Priority"
                  size="small"
                  renderValue={(selected) => {
                    const priorityConfig: Record<
                      string,
                      { icon: React.ReactNode; label: string }
                    > = {
                      high: {
                        icon: (
                          <ExpandLessIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        label: "High",
                      },
                      medium: {
                        icon: (
                          <DragHandleIcon
                            fontSize="medium"
                            sx={{ color: colors.Warning[500].$value }}
                          />
                        ),
                        label: "Medium",
                      },
                      low: {
                        icon: (
                          <ExpandMoreIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                        label: "Low",
                      },
                      highest: {
                        icon: (
                          <KeyboardDoubleArrowUpIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        label: "Highest",
                      },
                      lowest: {
                        icon: (
                          <KeyboardDoubleArrowDownIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                        label: "Lowest",
                      },
                    };
                    const config = priorityConfig[selected as string];
                    return config ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                        }}
                      >
                        {config.icon}
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.sm.$value,
                            color: colors.GrayLightMode[900].$value,
                          }}
                        >
                          {config.label}
                        </Typography>
                      </Box>
                    ) : (
                      // No selection → show placeholder with list icon
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                        }}
                      >
                        <FormatListBulletedIcon
                          fontSize="small"
                          sx={{ color: colors.GrayDarkMode[700].$value }}
                        />
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.sm.$value,
                            color: colors.GrayLightMode[900].$value,
                          }}
                        >
                          Select Priority
                        </Typography>
                      </Box>
                    );
                  }}
                  sx={{
                    borderRadius: spacing.Radius.md.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    "& .MuiSelect-select": {
                      paddingTop: spacing.Spacing.md1.$value,
                      paddingBottom: spacing.Spacing.md1.$value,
                      color: colors.GrayLightMode[900].$value,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  }}
                >
                  {[
                    {
                      value: "high",
                      label: "High",
                      icon: (
                        <ExpandLessIcon
                          fontSize="medium"
                          sx={{ color: colors.Warning[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "medium",
                      label: "Medium",
                      icon: (
                        <DragHandleIcon
                          fontSize="medium"
                          sx={{ color: colors.Warning[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "low",
                      label: "Low",
                      icon: (
                        <ExpandMoreIcon
                          fontSize="medium"
                          sx={{ color: colors.Blue[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "highest",
                      label: "Highest",
                      icon: (
                        <KeyboardDoubleArrowUpIcon
                          fontSize="medium"
                          sx={{ color: colors.Error[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "lowest",
                      label: "Lowest",
                      icon: (
                        <KeyboardDoubleArrowDownIcon
                          fontSize="medium"
                          sx={{ color: colors.Blue[500].$value }}
                        />
                      ),
                    },
                  ].map(({ value, label, icon }) => (
                    <MenuItem key={value} value={value}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                        }}
                      >
                        {icon}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Website */}
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    background: colors.Base.White.$value,
                    paddingRight: spacing.Spacing.xs.$value,
                    "&.Mui-focused": {
                      color: colors.Brand[600].$value,
                    },
                  }}
                >
                  Website (optional)
                </InputLabel>
                <Select
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  displayEmpty
                  label="Website (optional)"
                  size="small"
                  startAdornment={
                    <InputAdornment position="start">
                      <LanguageIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayDarkMode[700].$value }}
                      />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: spacing.Radius.md.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    "& .MuiSelect-select": {
                      paddingTop: spacing.Spacing.md1.$value,
                      paddingBottom: spacing.Spacing.md1.$value,
                      color: colors.GrayLightMode[900].$value,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  }}
                >
                  <MenuItem value="">Select Website</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                </Select>
              </FormControl>

              {/* Info banner */}
              <Box
                sx={{
                  backgroundColor: colors.Brand[50].$value,
                  border: `1px solid ${colors.Brand[500].$value}`,
                  borderRadius: spacing.Radius.md.$value,
                  padding: `${spacing.Spacing.md.$value} ${spacing.Spacing.xl.$value}`,
                  marginBlock: spacing.Spacing.xl.$value,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: spacing.Spacing.md.$value,
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    lineHeight: typography.Line_height.text.xs.$value,
                    color: colors.Brand[600].$value,
                    textAlign: "center",
                  }}
                >
                  💡 These values will apply to all tasks unless you override
                  them individually.
                </Typography>
              </Box>
            </Box>

            {/* SKIP / NEXT button pinned to bottom */}
            <Box sx={{ m: spacing.Spacing.xl.$value }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() =>
                  buttonLabel === "SAVE" ? onClose() : handleProceed()
                }
                sx={{
                  backgroundColor: colors.Brand[600].$value,
                  borderRadius: spacing.Radius.xs.$value,
                  textTransform: "none",
                  fontWeight: typography.Font_weight.Medium.$value,
                  fontSize: typography.Size.Text.sm.$value,
                  lineHeight: typography.Line_height.text.md.$value,
                  py: spacing.Spacing.md.$value,
                  "&:hover": { backgroundColor: colors.Brand[600].$value },
                }}
              >
                {buttonLabel}
              </Button>
            </Box>
          </>
        ) : (
          <AddTasks
            sharedProps={allSharedFilled ? sharedProps : undefined}
            onBack={() => setStep("shared")}
            onSave={() => onClose()}
          />
        )}
      </Drawer>
    </LocalizationProvider>
  );
}
